import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import API_URL, { isProdEnv, secretEncryptionKey } from "../ServiceModule/Service";
import { BASE_URL } from "./apiConstant";
import { logout } from "../Features/services/authSlice";
import { showDialog } from "../Features/services/authSlice";
import encryptJSON from "../ServiceModule/Encrypt";

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {

    api.dispatch(logout());
    api.dispatch(showDialog(true));
  }
  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Post", "Cart", "cartData", "notification"],
  endpoints: (builder) => ({
    postAPI: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: convertBody(formData),
      }),
      invalidatesTags: ["Post", "Cart"],
    }),
    postApiMultiPartData: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: "POST",
        headers: {
          "Content-Type": "multipart/form-data",
        },
        body: convertBody(formData),
      }),
      invalidatesTags: ["Post", "Cart"],
    }),
    postLoginAPI: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: { userPhone: formData },
      }),
    }),
    postAPIWithToken: builder.mutation({
      query: ({ url, formData, token }) => ({
        url,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }),
      invalidatesTags: ["Post", "Cart"],
    }),
    postAPIWithOutToken: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: formData,
      }),
    }),
    postNotificationAPIWithOutToken: builder.mutation({
      query: ({ url, formData }) => ({
        url,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: formData,
      }),
      invalidatesTags: ["notification"]
    }),
    postNotificationAPIWithToken: builder.mutation({
      query: ({ url, formData, token }) => ({
        url,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }),
      invalidatesTags: ["notification"]
    }),
    orderpostAPIWithToken: builder.mutation({
      query: ({ url, formData, token }) => ({
        url,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }),
      invalidatesTags: ["cartData"],
    }),
    postAddToCartWithToken: builder.mutation({
      query: ({ url, formData, token }) => {
        let payloadData = formData
        if (isProdEnv == "true") {
          const encryptedPayload = encryptJSON(payloadData, secretEncryptionKey);
          payloadData = {
            element: encryptedPayload,
          }
        }
        return {
          url,
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payloadData),
        };
      },
      invalidatesTags: ["cartData"],
    }),

    removeFromCartWithToken: builder.mutation({
      query: ({ url, formData, token }) => {
        let payloadData = formData
        if (isProdEnv == "true") {
          const encryptedPayload = encryptJSON(payloadData, secretEncryptionKey);
          payloadData = {
            element: encryptedPayload,
          }
        }
        return {
          url,
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payloadData),
        };
      },
      invalidatesTags: ["cartData"],
    }),
    getAPI: builder.query({
      query: ({ url, token }) => ({
        url,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
      providesTags: ["Post", "Cart"],
    }),
    getNotificationAPI: builder.query({
      query: ({ url, token }) => ({
        url,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
      providesTags: ["notification"],
    }),
    getCartDataAPI: builder.query({
      query: ({ url, token }) => ({
        url,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
      providesTags: ["cartData"],
      keepUnusedDataFor: 0
    }),
    getAPIWithoutToken: builder.query({
      query: (url) => ({
        url,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      providesTags: ["Post", "Cart"],
    }),
    deleteAPI: builder.mutation({
      query: ({ url, token, _id }) => ({
        url,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: { _id: _id },
      }),
      invalidatesTags: ["Post", "Cart"],
    }),
    uploadAPI: builder.mutation({
      query: ({ url, formData, token }) => ({
        url,
        method: "POST",
        headers: {
          Accept: "*/*",
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }),
      invalidatesTags: ["Post", "Cart"],
    }),
  }),
});

export const {
  usePostAPIMutation,
  usePostLoginAPIMutation,
  usePostAPIWithTokenMutation,
  usePostAPIWithOutTokenMutation,
  useGetAPIQuery,
  useLazyGetAPIQuery,
  useGetAPIWithoutTokenQuery,
  useDeleteAPIMutation,
  useUploadAPIMutation,
  usePostApiMultiPartDataMutation,
  usePostAddToCartWithTokenMutation,
  useRemoveFromCartWithTokenMutation,
  useGetCartDataAPIQuery,
  useOrderpostAPIWithTokenMutation,
  usePostNotificationAPIWithOutTokenMutation,
  usePostNotificationAPIWithTokenMutation,
  useGetNotificationAPIQuery,
} = api;

export const convertBody = (formData) => {
  return Object.entries(formData)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
};
